/**
 * Cross-Tab Logout Synchronization Hook
 *
 * Uses BOTH BroadcastChannel (immediate, reliable) and storage events (fallback)
 * to detect when another tab logs out or changes session.
 *
 * This prevents the scenario where:
 * 1. User has Tab1 and Tab2 open
 * 2. User logs out in Tab1
 * 3. Tab2 still shows user data (security violation)
 *
 * With this hook, Tab2 will automatically redirect to login.
 *
 * @module hooks/useCrossTabLogout
 * @since 2026-01-21
 */

import { useEffect, useRef } from "react";
import { clearAllUserData } from "@/lib/session";

// BroadcastChannel name for auth synchronization
const AUTH_BROADCAST_CHANNEL = "lexe_auth_sync";

// Message types for BroadcastChannel
type AuthBroadcastMessage =
  | { type: "logout"; reason: string }
  | { type: "session_changed"; contactId: string };

/**
 * Broadcast logout to other tabs via BroadcastChannel
 * Call this BEFORE clearing local data
 */
export function broadcastLogout(reason: string = "user_logout"): void {
  try {
    if (typeof BroadcastChannel !== "undefined") {
      const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
      channel.postMessage({ type: "logout", reason } as AuthBroadcastMessage);
      channel.close();
      console.info("[crossTab] Broadcasted logout to other tabs");
    }
  } catch (e) {
    console.warn("[crossTab] Failed to broadcast logout:", e);
  }
}

/**
 * Broadcast session change to other tabs (e.g., when contactId changes)
 */
export function broadcastSessionChange(contactId: string): void {
  try {
    if (typeof BroadcastChannel !== "undefined") {
      const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
      channel.postMessage({
        type: "session_changed",
        contactId,
      } as AuthBroadcastMessage);
      channel.close();
      console.info("[crossTab] Broadcasted session change to other tabs");
    }
  } catch (e) {
    console.warn("[crossTab] Failed to broadcast session change:", e);
  }
}

/**
 * Hook to synchronize logout across browser tabs.
 *
 * Uses BOTH:
 * 1. BroadcastChannel (immediate, fires even in same-origin iframes)
 * 2. Storage event (fallback for browsers without BroadcastChannel)
 *
 * @param currentContactId - Current user's contact ID for session validation
 *
 * @example
 * ```tsx
 * function App() {
 *   const contactId = useAuthStore((s) => s.userInfo?.contactId);
 *   useCrossTabLogout(contactId);
 *   // ... rest of app
 * }
 * ```
 */
export function useCrossTabLogout(currentContactId?: string | null): void {
  const isRedirectingRef = useRef(false);

  useEffect(() => {
    // Guard to prevent multiple redirects
    const handleLogoutSync = (reason: string) => {
      if (isRedirectingRef.current) return;
      isRedirectingRef.current = true;

      console.info(`[crossTab] Logout sync triggered: ${reason}`);
      clearAllUserData();
      window.location.href = "/login";
    };

    // === BROADCAST CHANNEL (immediate, reliable) ===
    let authChannel: BroadcastChannel | null = null;

    if (typeof BroadcastChannel !== "undefined") {
      try {
        authChannel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);

        authChannel.onmessage = (event: MessageEvent<AuthBroadcastMessage>) => {
          const { type } = event.data;

          if (type === "logout") {
            handleLogoutSync("broadcast_logout");
            return;
          }

          if (type === "session_changed") {
            const { contactId } = event.data;
            // If contactId changed to a different user, logout this tab
            if (currentContactId && contactId !== currentContactId) {
              handleLogoutSync("broadcast_session_changed");
            }
          }
        };

        authChannel.onmessageerror = () => {
          console.warn("[crossTab] BroadcastChannel message error");
        };
      } catch (e) {
        console.warn("[crossTab] Failed to setup BroadcastChannel:", e);
      }
    }

    // === STORAGE EVENT (fallback, some edge cases) ===
    const handleStorageChange = (e: StorageEvent) => {
      // Detect when auth tokens are removed in another tab
      if (e.key === "lexe-auth-tokens" && e.newValue === null) {
        handleLogoutSync("storage_auth_tokens_cleared");
        return;
      }

      // Also detect when auth store is cleared
      if (e.key === "lexe-auth-store" && e.newValue === null) {
        handleLogoutSync("storage_auth_store_cleared");
        return;
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Cleanup
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      if (authChannel) {
        try {
          authChannel.close();
        } catch {
          // Ignore close errors
        }
      }
    };
  }, [currentContactId]);
}

export default useCrossTabLogout;
